import { Component } from '@theme/component';

const primaryVendors = ['floer', 'moduleo', 'mflor'];
const pinnedHandles = [
  'floer-walvisgraat-pvc-orka-onbehandeld-20',
  'floer-walvisgraat-pvc-bryde-bruin',
  'floer-walvisgraat-pvc-narwal-nootbruin',
];
const completedFeeds = new Map();
const initialPreviewThemeId = globalThis.location
  ? new URL(globalThis.location.href).searchParams.get('preview_theme_id') || (globalThis.Shopify?.theme?.role !== 'main' ? String(globalThis.Shopify?.theme?.id || '') : '')
  : '';

export function orderMatchingProducts(products) {
  const buckets = new Map([...primaryVendors, 'beautifloor', 'other', 'hamat'].map((vendor) => [vendor, []]));
  for (const product of products) {
    const vendor = String(product.vendor).trim().toLowerCase();
    buckets.get(buckets.has(vendor) ? vendor : 'other').push(product);
  }

  // Keep existing featured Floer designs first within their own brand.
  const pinRank = (product) => {
    const index = pinnedHandles.indexOf(product.handle);
    return index < 0 ? pinnedHandles.length : index;
  };
  buckets.get('floer').sort((a, b) => pinRank(a) - pinRank(b));

  const ordered = [];
  const secondary = buckets.get('beautifloor');
  const rounds = Math.max(...primaryVendors.map((vendor) => buckets.get(vendor).length));
  for (let round = 0; round < rounds; round++) {
    for (const vendor of primaryVendors) {
      const product = buckets.get(vendor)[round];
      if (product) ordered.push(product);
    }
    // Give Beautifloor one introduction after the first primary-brand round.
    if (round === 0 && secondary.length) ordered.push(secondary[0]);
  }
  ordered.push(...secondary.slice(rounds ? 1 : 0), ...buckets.get('other'), ...buckets.get('hamat'));
  return ordered;
}

export function matchingFeedURL(href, sort, page = 1) {
  const url = new URL(href);
  url.hash = '';
  url.searchParams.delete('sections');
  url.searchParams.delete('section_id');
  url.searchParams.set('view', 'smid-brand-data');
  url.searchParams.set('sort_by', sort);
  url.searchParams.set('page', String(page));
  if (url.pathname.replace(/\/$/, '').endsWith('/search')) {
    const types = url.searchParams.get('type');
    if (!types || types.split(',').includes('product')) url.searchParams.set('type', 'product');
  }
  if (!url.searchParams.has('preview_theme_id') && initialPreviewThemeId) url.searchParams.set('preview_theme_id', initialPreviewThemeId);
  return url;
}

export function matchingCardsURL(href, sort, page) {
  const url = matchingFeedURL(href, sort, page);
  url.searchParams.set('view', 'smid-brand-cards');
  return url;
}

export async function fetchMatchingPage(url, signal, fetchFunction = fetch, timeout = 20000) {
  for (let attempt = 0; attempt < 2; attempt++) {
    const requestSignal = AbortSignal.any([signal, AbortSignal.timeout(timeout)]);
    const response = await fetchFunction(url, { signal: requestSignal, credentials: 'same-origin' });
    if (response.ok) return response.json();
    if (attempt === 1 || (response.status !== 429 && response.status < 500)) throw new Error('Catalog request failed (HTTP ' + response.status + ')');
    await new Promise((resolve, reject) => {
      if (signal.aborted) return reject(new DOMException('Aborted', 'AbortError'));
      const onAbort = () => { clearTimeout(timer); reject(new DOMException('Aborted', 'AbortError')); };
      const timer = setTimeout(() => { signal.removeEventListener('abort', onAbort); resolve(); }, 400);
      signal.addEventListener('abort', onAbort, { once: true });
    });
  }
}

export async function loadMatchingProducts(fetchPage, context, signal, onProgress = () => {}) {
  const first = await fetchPage(1, signal);
  const pages = Math.max(1, Number(first.pages));
  const total = Number(first.total);
  if (!Number.isSafeInteger(pages) || !Number.isSafeInteger(total) || total < 0 || total > 25000 || pages !== Math.max(1, Math.ceil(total / 250))) throw new Error('Incomplete catalog metadata');
  const results = new Map();
  const accept = (data, expectedPage) => {
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
    if (data.page !== expectedPage || data.pages !== first.pages || data.total !== first.total || data.context !== context || data.sort !== first.sort || !Array.isArray(data.products)) throw new Error('Catalog changed while loading');
    const expectedSize = Math.min(250, Math.max(0, total - (expectedPage - 1) * 250));
    if (data.size !== expectedSize || data.products.length > data.size || (context === 'collection' && data.products.length !== data.size)) throw new Error('Incomplete catalog page');
    for (const product of data.products) {
      if (!Number.isSafeInteger(product.id) || typeof product.handle !== 'string' || typeof product.vendor !== 'string' || (product.html !== undefined && typeof product.html !== 'string')) throw new Error('Invalid product record');
    }
    results.set(expectedPage, data.products.map((product) => ({ ...product, sourcePage: expectedPage })));
    onProgress(results.size, pages);
  };
  accept(first, 1);
  let nextPage = 2;
  await Promise.all(Array.from({ length: Math.min(3, pages - 1) }, async () => {
    while (nextPage <= pages) {
      const page = nextPage++;
      accept(await fetchPage(page, signal), page);
    }
  }));
  const products = [...results.entries()].sort((a, b) => a[0] - b[0]).flatMap(([, productsOnPage]) => productsOnPage);
  if (results.size !== pages || new Set(products.map((product) => product.id)).size !== products.length) throw new Error('Incomplete or duplicate product results');
  const ordered = orderMatchingProducts(products);
  Object.defineProperty(ordered, 'feed', { value: {
    context: first.context, sort: first.sort, pages: first.pages, total: first.total,
    pageIds: new Map([...results.entries()].map(([page, items]) => [page, items.map((item) => item.id)])),
  } });
  return ordered;
}

export async function hydrateMatchingCards(products, count, fetchPage, cardPages, signal) {
  const wanted = products.slice(0, count);
  const pending = [...new Set(wanted.map((product) => product.sourcePage))].filter((page) => !cardPages.has(page));
  let next = 0;
  await Promise.all(Array.from({ length: Math.min(3, pending.length) }, async () => {
    while (next < pending.length) {
      const page = pending[next++];
      const data = await fetchPage(page, signal);
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
      const expectedIds = products.feed.pageIds.get(page);
      const expectedSize = Math.min(250, Math.max(0, products.feed.total - (page - 1) * 250));
      if (data.page !== page || data.pages !== products.feed.pages || data.total !== products.feed.total || data.context !== products.feed.context || data.sort !== products.feed.sort || data.size !== expectedSize || !Array.isArray(data.products)) throw new Error('Product cards changed while loading');
      if (data.products.length !== expectedIds.length || data.products.some((product, index) => product.id !== expectedIds[index] || typeof product.html !== 'string')) throw new Error('Product card identities changed while loading');
      cardPages.set(page, new Map(data.products.map((product) => [product.id, product.html])));
    }
  }));
  return wanted.map((product) => ({ ...product, html: cardPages.get(product.sourcePage).get(product.id) }));
}

class SmidBrandResults extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.onClick);
    document.addEventListener('shopify:search:update', this.onNativeUpdate);
    document.addEventListener('shopify:collection:update', this.onNativeUpdate);
    queueMicrotask(() => this.start());
  }

  updatedCallback() {
    super.updatedCallback();
    queueMicrotask(() => this.start(true));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.onClick);
    document.removeEventListener('shopify:search:update', this.onNativeUpdate);
    document.removeEventListener('shopify:collection:update', this.onNativeUpdate);
    this.updateGeneration = (this.updateGeneration || 0) + 1;
    this.controller?.abort();
    this.requestKey = null;
  }

  onNativeUpdate = (event) => {
    const section = event.target?.closest?.('[id^="shopify-section-"]');
    if (section && section.id !== 'shopify-section-' + this.dataset.sectionId) return;
    const generation = this.updateGeneration = (this.updateGeneration || 0) + 1;
    this.controller?.abort();
    this.requestKey = null;
    Promise.resolve(event.promise || event.detail?.promise).then(() => {
      if (this.isConnected && generation === this.updateGeneration) this.start(true);
    }).catch(() => {});
  };

  onClick = (event) => {
    if (event.target.closest('[data-priority-retry]')) {
      if (this.visibleProducts) this.showMore();
      else this.start(true, true);
    }
    if (event.target.closest('[data-priority-more]')) {
      this.showMore();
    }
  };

  async showMore() {
    if (this.loadingMore || !this.products) return;
    this.loadingMore = true;
    const controller = this.controller;
    const more = this.querySelector('[data-priority-more]');
    more.disabled = true;
    this.querySelector('[data-priority-retry]').hidden = true;
    const targetCount = Math.min(this.products.length, this.visibleCount + this.pageSize);
    try {
      const visible = await hydrateMatchingCards(this.products, targetCount, (page, signal) => fetchMatchingPage(matchingCardsURL(this.requestKey, this.dataset.sort, page), signal), this.cardPages, controller.signal);
      if (!this.isCurrent(controller)) return;
      this.visibleCount = targetCount;
      this.visibleProducts = visible;
      this.render();
    } catch (error) {
      if (!this.isCurrent(controller) || error.name === 'AbortError') return;
      console.warn('[Smid brand order]', error);
      this.querySelector('[data-priority-status]').textContent = this.dataset.moreErrorLabel;
      this.querySelector('[data-priority-retry]').hidden = false;
    } finally {
      if (this.controller === controller) {
        this.loadingMore = false;
        more.disabled = false;
      }
    }
  }

  isCurrent(controller) {
    return this.isConnected && this.controller === controller && !controller.signal.aborted && matchingFeedURL(location.href, this.dataset.sort).href === this.requestKey;
  }

  async start(morphed = false, retry = false) {
    if (!this.isConnected) return;
    const requestURL = matchingFeedURL(location.href, this.dataset.sort);
    const key = requestURL.href;
    if (key === this.requestKey && !retry) {
      if (morphed && this.visibleProducts) this.render(true);
      return;
    }
    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;
    this.requestKey = key;
    this.products = null;
    this.visibleProducts = null;
    this.cardPages = new Map();
    this.loadingMore = false;
    this.pageSize = Math.max(1, Number(this.dataset.pageSize) || 24);
    this.visibleCount = this.pageSize;
    const panel = this.querySelector('[data-priority-panel]');
    const fallback = this.querySelector('[data-priority-fallback]');
    const status = this.querySelector('[data-priority-status]');
    const fallbackGrid = fallback.querySelector('.main-collection-grid');
    if (fallbackGrid) fallbackGrid.id = 'NativeResultsList-' + this.dataset.sectionId;
    status.id = 'ResultsList';
    status.tabIndex = -1;
    panel.hidden = false;
    fallback.hidden = false;
    this.querySelector('[data-priority-mount]').replaceChildren();
    this.querySelector('[data-priority-retry]').hidden = true;
    this.querySelector('[data-priority-more]').hidden = true;
    status.textContent = this.dataset.loadingLabel;
    panel.setAttribute('aria-busy', 'true');
    const current = () => this.isCurrent(controller);
    try {
      let products = retry ? null : completedFeeds.get(key);
      if (!products) {
        const fetchPage = (page, signal) => fetchMatchingPage(matchingFeedURL(requestURL.href, this.dataset.sort, page), signal);
        products = await loadMatchingProducts(fetchPage, this.dataset.context, controller.signal);
        if (!current()) return;
        completedFeeds.set(key, products);
        while (completedFeeds.size > 4) completedFeeds.delete(completedFeeds.keys().next().value);
      }
      if (!current()) return;
      this.products = products;
      const visibleProducts = await hydrateMatchingCards(products, this.visibleCount, (page, signal) => fetchMatchingPage(matchingCardsURL(requestURL.href, this.dataset.sort, page), signal), this.cardPages, controller.signal);
      if (!current()) return;
      this.visibleProducts = visibleProducts;
      this.render(true);
    } catch (error) {
      if (!current() || error.name === 'AbortError') return;
      console.warn('[Smid brand order]', error);
      controller.abort();
      fallback.hidden = false;
      status.removeAttribute('id');
      if (fallbackGrid) fallbackGrid.id = 'ResultsList';
      panel.setAttribute('aria-busy', 'false');
      status.textContent = this.dataset.errorLabel;
      this.querySelector('[data-priority-retry]').hidden = false;
    }
  }

  render(rebuild = false) {
    if (!this.visibleProducts || !this.isConnected) return;
    const fallback = this.querySelector('[data-priority-fallback]');
    const mount = this.querySelector('[data-priority-mount]');
    const panel = this.querySelector('[data-priority-panel]');
    if (rebuild || !mount.querySelector('.product-grid')) {
      const original = fallback.querySelector('.main-collection-grid');
      const originalGrid = original?.querySelector('.product-grid');
      if (!original || !originalGrid) throw new Error('Native product grid is unavailable');
      const outer = original.cloneNode(false);
      outer.id = 'ResultsList';
      outer.tabIndex = -1;
      const inner = originalGrid.parentElement.cloneNode(false);
      const grid = originalGrid.cloneNode(false);
      grid.removeAttribute('ref');
      grid.removeAttribute('data-last-page');
      inner.append(grid);
      outer.append(inner);
      mount.replaceChildren(outer);
    }
    const grid = mount.querySelector('.product-grid');
    const fragment = document.createDocumentFragment();
    for (const product of this.visibleProducts) {
      const item = document.createElement('li');
      item.className = 'product-grid__item sbp-product';
      item.dataset.productId = String(product.id);
      item.dataset.vendor = product.vendor;
      item.innerHTML = product.html;
      fragment.append(item);
    }
    grid.replaceChildren(fragment);
    panel.hidden = false;
    panel.setAttribute('aria-busy', 'false');
    fallback.hidden = true;
    const originalResults = fallback.querySelector('.main-collection-grid');
    if (originalResults) originalResults.id = 'NativeResultsList-' + this.dataset.sectionId;
    this.querySelector('[data-priority-status]').removeAttribute('id');
    this.querySelector('[data-priority-status]').textContent = '';
    this.querySelector('[data-priority-more]').hidden = this.visibleCount >= this.products.length;
    this.querySelector('[data-priority-retry]').hidden = true;
  }
}

if (!customElements.get('smid-brand-results')) customElements.define('smid-brand-results', SmidBrandResults);
